import { capitalize } from 'lodash'
import moment from 'moment'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { useWindowResize } from '../../hooks/useWindowResize'
import { CalendarFilter } from '../../models/CalendarFilter'
import { Item } from '../../models/Item'
import { User } from '../../models/User'
import {
    AvailabilityFilter,
    CalendarView,
    calendarViews,
    getDateLabelForView,
    getSkipToDateForView,
} from '../../utils/calendar'
import { currencyFormat, formatDuration } from '../../utils/numbers'
import { Icon, IconName } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import { SelectListItem } from '../common/SelectList'
import { SelectModal } from '../common/SelectModal'
import { SearchField } from '../common/fields/SearchField'
import { Select } from '../common/fields/Select'
import { CalendarAvailabilityFilters } from './CalendarAvailabilityFilters'
import { CalendarEmptyState } from './CalendarEmptyState'
import { CalendarFilterButton } from './CalendarFilterButton'

type Props = {
    view: CalendarView
    date: moment.Moment
    pros: User[]
    items: Item[]
    filter: CalendarFilter
    availabilityFilter: AvailabilityFilter
    searchText: string
    setDate: (date: moment.Moment) => void
    setView: (view: CalendarView) => void
    onUpdateFilter: (filter: CalendarFilter) => void
    onSearch: (query: string) => void
    onClearSearch: () => void
}

export const CalendarHeaderBar = ({
    view,
    date,
    pros,
    items,
    filter,
    availabilityFilter,
    searchText,
    setDate,
    setView,
    onUpdateFilter,
    onSearch,
    onClearSearch,
}: Props) => {
    const owner = PSApplication.owner()
    const navigate = usePSNavigate()

    const dateLabel = useMemo(() => getDateLabelForView(date, view), [date, view])

    const selectClasses: SelectListItem[] = useMemo(() => {
        return items
            .filter((i) => !!i.id && !!i.title && i.type === 'class')
            .map((i) => ({
                id: i.id!,
                title: i.title!,
                subtitle: `${currencyFormat(i.rate)} ${i.duration ? `· ${formatDuration(i.duration)}` : ''}`,
                tertiaryView: i.image?.url ? (
                    <img className="size-12 rounded-full" alt={i.name} src={i.image.url} />
                ) : (
                    <Icon icon="student.large" background="round" />
                ),
            }))
    }, [items])

    const [isCompact, setIsCompact] = useState(false)
    const [isCreationMenuOpen, setIsCreationMenuOpen] = useState(false)
    const [showSelectClassModal, setShowSelectClassModal] = useState(false)
    const [selectedClass, setSelectedClass] = useState<SelectListItem | null>(null)

    const { width } = useWindowResize()
    const calendarHeaderRef = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        if (!calendarHeaderRef.current) return
        const COMPACT_WIDTH = 1240
        setIsCompact(width <= COMPACT_WIDTH)
    }, [width])

    const creationOptions = [
        {
            id: 'schedule',
            title: 'Appointment',
            icon: 'user' as IconName,
        },
        {
            id: 'lesson',
            title: 'Lesson',
            icon: 'book' as IconName,
        },
        // {
        //     id: 'task',
        //     title: 'Task',
        //     icon: 'notepadCheckmark' as IconName,
        // },
        {
            id: 'block',
            title: 'Block',
            icon: 'clockLock' as IconName,
        },
    ]

    const onSelectCreationOptionId = (id: string) => {
        setIsCreationMenuOpen(false)

        switch (id) {
            case 'lesson':
                setShowSelectClassModal(true)
                break
            case 'task':
                // TODO: task backport
                break
            case 'block':
                navigate('/blocks/new')
                break
            case 'schedule':
                navigate('/bookings/new')
                break
        }
    }

    return (
        <>
            <div className="flex items-center justify-between gap-6 px-8" ref={calendarHeaderRef}>
                {!['search', 'availability'].includes(view) && (
                    <div className="flex items-center gap-6">
                        <div className="flex gap-4">
                            <Button size="small" onClick={() => setDate(moment())}>
                                <Label size="sm" className="text-primary-700 pointer-events-none">
                                    Today
                                </Label>
                            </Button>

                            <div className="h-9 w-px bg-gray-300"></div>

                            <div className="flex gap-2">
                                <Button
                                    shape="square"
                                    size="small"
                                    onClick={() => setDate(getSkipToDateForView(date, view, -1))}
                                >
                                    <Icon icon="chevronLeft.small" />
                                </Button>

                                <Button
                                    shape="square"
                                    size="small"
                                    onClick={() => setDate(getSkipToDateForView(date, view))}
                                >
                                    <Icon icon="chevronRight.small" />
                                </Button>
                            </div>
                        </div>

                        <Heading size={isCompact ? 'sm' : 'md'} className="truncate text-gray-700">
                            {dateLabel}
                        </Heading>
                    </div>
                )}

                {view === 'availability' && (
                    <CalendarAvailabilityFilters
                        availabilityFilter={availabilityFilter}
                        pros={pros}
                        items={items}
                    />
                )}

                {view === 'search' && (
                    <SearchField
                        className="w-[378px]"
                        value={searchText}
                        onSearch={onSearch}
                        onClear={onClearSearch}
                        autoFocus
                    />
                )}

                <div className="flex items-center gap-2">
                    {!['search', 'availability'].includes(view) && (
                        <Button size="small" onClick={() => setView('search')}>
                            <Icon icon="magnifier.small" />
                            {!isCompact && (
                                <Label size="sm" className="text-primary-700 pointer-events-none">
                                    Search
                                </Label>
                            )}
                        </Button>
                    )}

                    {view !== 'availability' && (
                        <CalendarFilterButton
                            filter={filter}
                            pros={pros}
                            items={items}
                            compact={isCompact}
                            updateFilter={onUpdateFilter}
                        />
                    )}

                    {view !== 'search' && (
                        <Select
                            align="end"
                            options={calendarViews
                                .filter((v) => !['search', 'availability'].includes(v))
                                .map((v) => ({
                                    id: v,
                                    title: v === '4day' ? '4 Days' : capitalize(v),
                                }))}
                            selectedOptionID={view}
                            onSelectOption={(option) => setView(option.id)}
                        />
                    )}

                    {view !== 'search' && (
                        <Select
                            align="end"
                            trigger={
                                <Button
                                    size="small"
                                    intent="primary"
                                    leftIcon="plus.small"
                                    rightIcon={
                                        <Icon
                                            icon="chevronDown.small"
                                            className={`transition-transform duration-200 ${isCreationMenuOpen ? '-rotate-180' : 'rotate-0'}`}
                                            shouldInheritColor
                                        />
                                    }
                                >
                                    Create
                                </Button>
                            }
                            options={
                                owner.allowClass
                                    ? creationOptions
                                    : [...creationOptions.slice(0, 1), ...creationOptions.slice(2)]
                            }
                            onOpenChange={() => setIsCreationMenuOpen(true)}
                            onSelectOption={(option) => onSelectCreationOptionId(option.id)}
                        />
                    )}

                    {view === 'search' && (
                        <Button
                            shape="square"
                            size="small"
                            intent="tertiary_deprecated"
                            onClick={() => navigate(-1)}
                        >
                            <Icon icon="close.small" />
                        </Button>
                    )}
                </div>
            </div>

            <SelectModal
                title="Select Class"
                isOpen={showSelectClassModal}
                onClose={() => setShowSelectClassModal(false)}
                onDone={() => navigate(`/settings/classes/${selectedClass?.id}/lessons`)}
                selectionType="single"
                items={selectClasses}
                selectedItems={!!selectedClass ? [selectedClass] : []}
                onSelectItems={(items) => setSelectedClass(items.length ? items[0] : null)}
                canSearch={selectClasses.length > 6}
                emptyView={
                    <CalendarEmptyState
                        src={'/icons/checklist.svg'}
                        title="No Classes"
                        subtitle="You haven't added any classes yet"
                    />
                }
            />
        </>
    )
}
